import {
  resolveNotificationActionType,
  resolveNotificationTarget,
} from "./transformNotification";
import {
  isNudgeBadge,
  isNudgingCommitment,
  isOneShotPromiseBadge,
  commitmentHasAnswerableQuestion,
  isSlackBadgeCommitment,
  resolveCommittedNudgeTotal,
  resolveNudgeCyclesAnsweredCount,
  resolveNudgeSummaryDisplay,
  resolveQuestionsFromCommitment,
} from "./badgeQuestions";
import { isUserCommitterForNudgeActions } from "./commitmentFilters";
import {
  normalizeApiId,
  resolveCommitmentApiId,
} from "./commitmentIds";

/** Shown when an issuer tries to Issue before every nudge is answered. */
export const ISSUER_CANNOT_ISSUE_UNANSWERED_NUDGES_MSG =
  "All nudges must be answered before you can issue this badge.";

/**
 * Invitation id from a getMessagesApp row (dbId when actionType is invitation, etc.).
 */
export const getInvitationIdFromNotificationRow = (
  notification: any
): string | null => {
  const t = resolveNotificationTarget(notification);
  if (t.actionable && t.targetType === "invitation") {
    return String(t.targetId);
  }
  return null;
};

const norm = (s: string) => String(s).trim();

/** Best-effort: API may store invitation linkage on commitments under different keys. */
export const extractInvitationIdsFromCommitment = (c: any): string[] => {
  const ids: string[] = [];
  const push = (v: unknown) => {
    if (v == null || v === "") return;
    if (typeof v === "object" && v !== null) {
      const o = v as Record<string, unknown>;
      if (o._id != null) ids.push(norm(String(o._id)));
      if (o.id != null) ids.push(norm(String(o.id)));
    } else {
      ids.push(norm(String(v)));
    }
  };

  push(c?.invitationId);
  push(c?.invitation_id);
  push(c?.nmInvitationId);
  push(c?.invitation);
  if (c?.invitation && typeof c.invitation === "object") {
    push((c.invitation as any)._id);
    push((c.invitation as any).id);
  }
  if (c?.extraData && typeof c.extraData === "object") {
    const ex = c.extraData as Record<string, unknown>;
    push(ex.invitationId);
    push(ex.invitation_id);
    if (ex.invitation && typeof ex.invitation === "object") {
      const inv = ex.invitation as Record<string, unknown>;
      push(inv._id);
      push(inv.id);
    }
  }

  return [...new Set(ids.filter(Boolean))];
};

export const commitmentReferencesInvitationId = (
  myCommitments: any[],
  invitationId: string
): boolean => {
  const want = norm(invitationId);
  return myCommitments.some((c) =>
    extractInvitationIdsFromCommitment(c).some((id) => id === want)
  );
};

/**
 * Show Accept/Reject only when this invitation is still pending in app data, and we are
 * not already showing a commitment created from that invitation (e.g. accepted on another client).
 */
export const invitationNotificationShouldShowActions = (
  rawNotification: any,
  receivedInvitations: any[],
  myCommitments: any[]
): boolean => {
  const invId = getInvitationIdFromNotificationRow(rawNotification);
  if (!invId) return false;

  if (commitmentReferencesInvitationId(myCommitments, invId)) {
    return false;
  }

  const pendingInvitation = receivedInvitations.some(
    (inv) =>
      norm(String(inv._id || inv.id)) === norm(invId) &&
      String(inv.status || "").toLowerCase() === "pending"
  );
  if (pendingInvitation) {
    return true;
  }
  // WhatsApp parity: trust actionable invitation rows until lists refresh.
  return rawNotification?.actionable === "yes";
};

/** Member offer to the issuer — same as Actions → "Received Promise Offers" / getBadgeRequestsForActions. */
export const isPendingAcceptanceStatus = (status: unknown): boolean =>
  String(status || "")
    .trim()
    .toLowerCase()
    .replace(/_/g, "-") === "pending-acceptance";

/**
 * Issuer must Issue / Deny / Clarify — same as pending approval badge requests in
 * receivedCommitments (API may use hyphen or underscore).
 */
export const isPendingApprovalStatus = (status: unknown): boolean =>
  String(status || "")
    .toLowerCase()
    .replace(/-/g, "_") === "pending_approval";

function countNonEmptyStrings(list: unknown): number {
  if (!Array.isArray(list)) return 0;
  return list.filter((x) => String(x ?? "").trim().length > 0).length;
}

/** How many badge-request explanations the committer has submitted. */
export function countBadgeExplanationEntries(commitment: any): number {
  const fromList = countNonEmptyStrings(
    commitment?.extraData?.explanations ?? commitment?.explanations
  );
  if (fromList > 0) return fromList;
  const singular = String(
    commitment?.explanation ?? commitment?.extraData?.explanation ?? ""
  ).trim();
  return singular ? 1 : 0;
}

/** How many issuer clarifications have been sent. */
export function countBadgeExpectationEntries(commitment: any): number {
  return countNonEmptyStrings(
    commitment?.extraData?.expectations ?? commitment?.expectations
  );
}

/**
 * Issuer sent Clarify (re-start). The committer must submit Request Badge again
 * with a new explanation before the issuer can Issue / Reject / Clarify again.
 */
export function isAwaitingBadgeRerequestAfterClarify(commitment: any): boolean {
  const expectations = countBadgeExpectationEntries(commitment);
  if (expectations === 0) return false;
  return expectations >= countBadgeExplanationEntries(commitment);
}

/**
 * Any pending-approval badge request visible on the issuer's Badge Requests tab,
 * including rows awaiting a new explanation after Clarify.
 */
export function isIssuerBadgeRequestVisible(commitment: any): boolean {
  return isPendingApprovalStatus(commitment?.status);
}

/**
 * Issuer can act (Issue / Reject / Clarify) — pending approval with a fresh
 * explanation that has not yet been clarified away.
 */
export function isActiveIssuerBadgeRequest(commitment: any): boolean {
  if (!isPendingApprovalStatus(commitment?.status)) return false;
  return !isAwaitingBadgeRerequestAfterClarify(commitment);
}

/**
 * Accept / Reject (offer): commitment must still be pending-acceptance for the issuer.
 *
 * When `knownReceivedCommitmentIds` includes the target, trust commitment status only —
 * do not fall back to `actionable === "yes"`. Backend messages often stay actionable after
 * accept/decline, which would leave stale Accept/Reject in Action Required forever.
 */
export const offerNotificationShouldShowActions = (
  rawNotification: any,
  pendingOfferCommitmentIds: Set<string>,
  knownReceivedCommitmentIds?: Set<string>
): boolean => {
  const target = resolveNotificationTarget(rawNotification);
  const routing = resolveNotificationActionType(
    rawNotification,
    target.actionable ? target.targetType : null
  );
  if (routing !== "offer") {
    return true;
  }
  if (!target.actionable || target.targetType !== "commitment") {
    return false;
  }
  const id = normalizeApiId(target.targetId);
  if (!id) return false;
  if (pendingOfferCommitmentIds.has(id)) {
    return true;
  }
  // We know this commitment and it is no longer pending-acceptance → stale.
  if (knownReceivedCommitmentIds?.has(id)) {
    return false;
  }
  // Commitment not in received list yet (still loading / ID lag) — trust server flag.
  return rawNotification?.actionable === "yes";
};

/**
 * Issue / Deny / Clarify (badge request): commitment must still be pending approval on the issuer side.
 * Once issued, denied, clarified, or status moves on, the message is treated as stale.
 *
 * Same rule as offers: if we already have the commitment in receivedCommitments and it is
 * not pending-approval (e.g. approved in Portfolio → Badges Issued), never keep Issue/Deny
 * just because the WhatsApp message still has actionable=yes.
 */
export const badgeRequestNotificationShouldShowActions = (
  rawNotification: any,
  pendingBadgeIssuerCommitmentIds: Set<string>,
  knownReceivedCommitmentIds?: Set<string>
): boolean => {
  const target = resolveNotificationTarget(rawNotification);
  const routing = resolveNotificationActionType(
    rawNotification,
    target.actionable ? target.targetType : null
  );
  if (routing !== "badge-request") {
    return true;
  }
  if (!target.actionable || target.targetType !== "commitment") {
    return false;
  }
  const id = normalizeApiId(target.targetId);
  if (!id) return false;
  if (pendingBadgeIssuerCommitmentIds.has(id)) {
    return true;
  }
  if (knownReceivedCommitmentIds?.has(id)) {
    return false;
  }
  return rawNotification?.actionable === "yes";
};

const normalizeCommitmentStatus = (status: unknown): string =>
  String(status ?? "")
    .trim()
    .toLowerCase()
    .replace(/_/g, "-");

const normalizeP2pState = (state: unknown): string =>
  String(state ?? "")
    .trim()
    .toLowerCase()
    .replace(/_/g, "-");

/** Best-effort question text from API list/detail shapes. */
export const resolveCurrentQuestionText = (commitment: any): string => {
  const qs = resolveQuestionsFromCommitment(commitment);
  return qs[0] ?? "";
};

/** Statuses that must not appear in "answer nudge" flows (not the same as all finalized). */
export const isTerminalForNudgeAnswering = (status: unknown): boolean => {
  const normalized = normalizeCommitmentStatus(status);
  return [
    "approved",
    "rejected",
    "reject",
    "declined",
    "decline",
    "denied",
    "deny",
    "pending-acceptance",
    "pending-approval",
  ].includes(normalized);
};

/** Remaining nudges the committer still owes (API uses several field names). */
export const resolveNudgesRemaining = (commitment: any): number | undefined => {
  if (!commitment || isOneShotPromiseBadge(commitment)) return undefined;

  const extra = commitment.extraData ?? {};
  const total = resolveCommittedNudgeTotal(commitment);
  const answered = resolveNudgeCyclesAnsweredCount(commitment);

  if (typeof total === "number" && total > 0) {
    const fromTotal = Math.max(0, total - answered);
    const apiLeft =
      typeof commitment.nudgesLeft === "number" &&
      !Number.isNaN(commitment.nudgesLeft)
        ? commitment.nudgesLeft
        : typeof extra.nudgesLeft === "number" && !Number.isNaN(extra.nudgesLeft)
          ? extra.nudgesLeft
          : undefined;
    // Never trust apiLeft alone when it disagrees with committed total − answered.
    if (typeof apiLeft === "number") {
      return Math.max(fromTotal, apiLeft);
    }
    return fromTotal;
  }

  if (
    typeof commitment.nudgesLeft === "number" &&
    !Number.isNaN(commitment.nudgesLeft)
  ) {
    return commitment.nudgesLeft;
  }

  if (
    typeof extra.nudgesLeft === "number" &&
    !Number.isNaN(extra.nudgesLeft)
  ) {
    return extra.nudgesLeft;
  }

  const toAnswerRaw =
    commitment.nudgesToAnswer ??
    extra.nudgesToAnswer ??
    commitment.totalNudges;

  if (typeof toAnswerRaw === "number" && !Number.isNaN(toAnswerRaw)) {
    return Math.max(0, toAnswerRaw - answered);
  }

  if (
    typeof commitment.totalNudges === "number" &&
    commitment.totalNudges > answered
  ) {
    return commitment.totalNudges - answered;
  }

  return undefined;
};

/**
 * Commitments where the committer should submit an answer (p2p question or recurring nudge).
 * Used for Actions tabs, notifications, and dashboard routing.
 */
export const commitmentNeedsAnswer = (commitment: any): boolean => {
  if (!commitment) return false;

  const status = normalizeCommitmentStatus(commitment.status);
  if (isTerminalForNudgeAnswering(status)) {
    return false;
  }

  if (isOneShotPromiseBadge(commitment)) {
    const question = resolveCurrentQuestionText(commitment);
    const isQuestionStatus =
      status === "question" ||
      status === "questions" ||
      status === "awaiting-answer";
    return Boolean(question && isQuestionStatus);
  }

  const p2pState = normalizeP2pState(commitment.p2pState);
  const question = resolveCurrentQuestionText(commitment);
  const badgeType = String(
    commitment?.badge?.type ?? commitment?.type ?? ""
  )
    .trim()
    .toLowerCase();

  const isQuestionStatus =
    status === "question" ||
    status === "questions" ||
    status === "awaiting-answer";

  const isAnsweringP2p =
    !p2pState ||
    p2pState === "answering" ||
    p2pState === "answer" ||
    p2pState === "answer-question" ||
    p2pState === "waiting-for-answer";

  if (isQuestionStatus && isAnsweringP2p) {
    return true;
  }

  if (question && (badgeType === "p2p" || commitment?.badge?.questionsTimed)) {
    return true;
  }

  if (
    commitment?.requiresAnswer === true ||
    commitment?.actionRequired === true
  ) {
    return true;
  }

  const nudgesRemaining = resolveNudgesRemaining(commitment);
  if (typeof nudgesRemaining === "number" && nudgesRemaining > 0) {
    return true;
  }

  if (isNudgeBadge(commitment?.badge, commitment?.type) || badgeType === "nudge") {
    if (question) {
      return true;
    }
    if (isQuestionStatus && isAnsweringP2p) {
      return true;
    }
    if (
      status === "active" ||
      status === "initiated" ||
      status === "in-progress" ||
      status === "inprogress"
    ) {
      return true;
    }
    return false;
  }

  return false;
};

/**
 * True when the committer still owes one or more nudge answers.
 * Issuers must not Issue a badge until every committed nudge is answered.
 */
export function hasUnansweredNudgesForIssuer(commitment: any): boolean {
  if (!commitment) return false;

  // One-shot badges (Promise Kept, etc.): block only while the single question is open.
  if (isOneShotPromiseBadge(commitment)) {
    return commitmentNeedsAnswer(commitment);
  }

  // Committer is actively owed an answer on a recurring nudge / p2p commitment.
  if (commitmentNeedsAnswer(commitment)) {
    const badgeType = String(
      commitment?.badge?.type ?? commitment?.type ?? ""
    )
      .trim()
      .toLowerCase();
    if (
      isNudgingCommitment(commitment) ||
      isNudgeBadge(commitment?.badge, commitment?.type) ||
      badgeType === "p2p" ||
      badgeType === "nudge"
    ) {
      return true;
    }
  }

  const summary = resolveNudgeSummaryDisplay(commitment);
  if (summary) {
    if (summary.total > 0 && !summary.allAnswered) {
      return true;
    }
    if (summary.pending > 0) {
      return true;
    }
  }

  const total = resolveCommittedNudgeTotal(commitment);
  const answered = resolveNudgeCyclesAnsweredCount(commitment);

  // Primary rule: committed nudge count vs completed cycles.
  if (typeof total === "number" && total > 0) {
    return answered < total;
  }

  const remaining = resolveNudgesRemaining(commitment);
  if (typeof remaining === "number" && remaining > 0) {
    return true;
  }

  if (isNudgingCommitment(commitment)) {
    if (summary && summary.total > 0 && summary.answered < summary.total) {
      return true;
    }
  }

  return false;
}

/** Issuer may Issue only when no nudge answers are still outstanding. */
export function issuerCanIssueBadge(commitment: any): boolean {
  return !hasUnansweredNudgesForIssuer(commitment);
}

/** Throws when the issuer must wait for remaining nudge answers. */
export function assertIssuerCanIssueBadge(commitment: any): void {
  if (!issuerCanIssueBadge(commitment)) {
    throw new Error(ISSUER_CANNOT_ISSUE_UNANSWERED_NUDGES_MSG);
  }
}

/** Invitation / pending-acceptance rows must not appear on Nudge Requests. */
export function isActionRequiredInvitationRow(commitment: any): boolean {
  if (!commitment) return false;
  const rowType = String(
    commitment.type ?? commitment.requestType ?? commitment.itemType ?? ""
  )
    .trim()
    .toLowerCase();
  if (rowType === "invitation" || rowType === "invite") return true;
  if (commitment.invitationId || commitment.invitation_id) return true;
  if (commitment.invitation && typeof commitment.invitation === "object") {
    return true;
  }
  const status = normalizeCommitmentStatus(commitment.status);
  return status === "pending-acceptance" || status === "initiated";
}

/** Row has recurring nudge work or a configured question the committer can answer now. */
function commitmentIsActiveNudgeWork(commitment: any): boolean {
  if (isNudgingCommitment(commitment)) return true;
  if (commitmentHasAnswerableQuestion(commitment)) return true;

  const status = normalizeCommitmentStatus(commitment.status);
  const isQuestionStatus =
    status === "question" ||
    status === "questions" ||
    status === "awaiting-answer";
  if (isQuestionStatus && resolveCurrentQuestionText(commitment)) {
    return true;
  }

  return false;
}

/**
 * Whether a row belongs on Actions → Nudge Requests.
 * - `fromActionRequired`: trust getActionRequired only for committer nudge work.
 * - Otherwise (myCommitments scan): server question state or visible question text.
 */
export function belongsInNudgeRequestsTab(
  commitment: any,
  options?: {
    fromActionRequired?: boolean;
    userId?: string | null;
    pendingApprovalCommitmentIds?: Set<string>;
  }
): boolean {
  if (!commitment) return false;
  if (isTerminalForNudgeAnswering(commitment.status)) return false;
  if (isOneShotPromiseBadge(commitment)) return false;
  if (isSlackBadgeCommitment(commitment)) return false;

  if (
    options?.userId &&
    !isUserCommitterForNudgeActions(commitment, options.userId)
  ) {
    return false;
  }

  const apiId = resolveCommitmentApiId(commitment);
  if (
    apiId &&
    options?.pendingApprovalCommitmentIds?.has(apiId)
  ) {
    return false;
  }

  if (isPendingApprovalStatus(commitment.status)) {
    return false;
  }

  if (options?.fromActionRequired) {
    if (isActionRequiredInvitationRow(commitment)) {
      return false;
    }
    // getActionRequired can include issuer badge-review rows — only trust nudge work.
    return commitmentIsActiveNudgeWork(commitment);
  }

  if (!commitmentNeedsAnswer(commitment)) return false;

  const status = normalizeCommitmentStatus(commitment.status);
  const isQuestionStatus =
    status === "question" ||
    status === "questions" ||
    status === "awaiting-answer";
  const p2pState = normalizeP2pState(commitment.p2pState);
  const isAnsweringP2p =
    !p2pState ||
    p2pState === "answering" ||
    p2pState === "answer" ||
    p2pState === "answer-question" ||
    p2pState === "waiting-for-answer";

  if (isQuestionStatus && isAnsweringP2p) {
    return commitmentIsActiveNudgeWork(commitment);
  }
  if (
    commitment?.requiresAnswer === true ||
    commitment?.actionRequired === true
  ) {
    return commitmentIsActiveNudgeWork(commitment);
  }

  return commitmentHasAnswerableQuestion(commitment);
}

/** Row on Nudge Requests tab should offer Answer (server list or local question). */
export function nudgeRequestRowNeedsAnswer(commitment: any): boolean {
  return belongsInNudgeRequestsTab(commitment, {
    fromActionRequired: commitment?._serverNeedsAnswer === true,
  });
}

/** @deprecated Prefer commitmentNeedsAnswer — kept for existing imports. */
export const isQuestionPendingCommitment = (commitment: any): boolean =>
  commitmentNeedsAnswer(commitment);

/**
 * Answer (question): commitment must still be in a question-pending state for the committer.
 */
export const questionNotificationShouldShowActions = (
  rawNotification: any,
  pendingQuestionCommitmentIds: Set<string>
): boolean => {
  const target = resolveNotificationTarget(rawNotification);
  const routing = resolveNotificationActionType(
    rawNotification,
    target.actionable ? target.targetType : null
  );
  if (routing !== "question" && routing !== "nudge") {
    return true;
  }
  if (!target.actionable || target.targetType !== "commitment") {
    return false;
  }
  const id = norm(String(target.targetId));
  if (pendingQuestionCommitmentIds.has(id)) {
    return true;
  }
  return false;
};
